import useCallbackState from '@/common/hooks/useCallbackState'
import { Box, Heading, ListItem, Text, UnorderedList } from '@chakra-ui/react'
import { format } from 'date-fns'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import useDateLocale from '@/common/hooks/useDateLocale'
import { trpc } from 'src/trpc'
import { useOnMeetingSummaryShared } from '../meetingSummaryShareEvents'

interface Props {
  meetingId: string
}

// « Already shared »: the circle's trace of each share of the compte rendu,
// with what was held back — visible to the circle only (#115)
export default function MeetingSummaryShares({ meetingId }: Props) {
  const { t } = useTranslation()
  const dateLocale = useDateLocale()
  const { call, value: shares } = useCallbackState(() =>
    trpc.meeting.getSummaryShares.query({ meetingId })
  )

  useEffect(() => {
    call()
  }, [meetingId])
  useOnMeetingSummaryShared(meetingId, call)

  if (!shares?.length) return null

  return (
    <Box>
      <Heading as="h3" size="sm" mb={2}>
        {t('MeetingSummaryShares.heading')}
      </Heading>
      <UnorderedList spacing={1}>
        {shares.map((share) => {
          const to = [
            ...share.recipientCircles.map((c) => c.name),
            share.recipientMembers.map((m) => m.name).join(', '),
          ]
            .filter(Boolean)
            .join(' — ')
          return (
            <ListItem key={share.id}>
              <Text as="span" fontSize="sm">
                {t(`MeetingSummaryShares.${share.kind}`, {
                  date: format(new Date(share.createdAt), 'PPp', {
                    locale: dateLocale,
                  }),
                  member: share.memberName ?? '—',
                  to,
                })}
              </Text>{' '}
              <Text
                as="span"
                fontSize="sm"
                color="gray.500"
                _dark={{ color: 'gray.300' }}
              >
                {t('MeetingSummaryShares.counts', {
                  removed: share.removedCount,
                  adjusted: share.adjustedCount,
                })}
              </Text>
            </ListItem>
          )
        })}
      </UnorderedList>
    </Box>
  )
}
